'use client';

import { useEffect, useCallback } from 'react';

/**
 * Hook for preserving scroll position and filters state
 * Optimized for Next.js App Router
 */
export function useScrollPreservation(citySlug: string) {
  // Save current scroll position
  const saveScrollPosition = useCallback(() => {
    const scrollKey = `scroll_${citySlug}`;
    sessionStorage.setItem(scrollKey, window.scrollY.toString());
  }, [citySlug]);

  // Restore scroll position for city page
  const restoreScrollPosition = useCallback(() => {
    const scrollKey = `scroll_${citySlug}`;
    const savedScrollY = sessionStorage.getItem(scrollKey);
    if (savedScrollY) {
      const scrollPosition = parseInt(savedScrollY, 10);
      // Use multiple strategies to ensure scroll restoration
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
      });
      // Fallback for slow rendering
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 100);
    }
  }, [citySlug]);

  // Set up scroll preservation
  useEffect(() => {
    // Disable browser's automatic scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    // Save scroll position on scroll (throttled)
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          saveScrollPosition();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Save scroll position when leaving the page
    const handleBeforeUnload = () => {
      saveScrollPosition();
    };

    // Add event listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleBeforeUnload);

    return () => {
      // Restore browser's default behavior
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
    };
  }, [saveScrollPosition]);

  return {
    saveScrollPosition,
    restoreScrollPosition,
  };
}
